/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import { motion } from "motion/react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

interface Props {
  children: ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  declare props: Props;
  declare setState: (state: Partial<State>) => void;

  state = { hasError: false, error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  private resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error!} resetError={this.resetError} />;
      }

      const isRtl = (typeof document !== "undefined" && document.documentElement.dir === "rtl");

      return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-[#0a0a0a]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-[#141414] border border-amber-500/20 rounded-2xl p-8 shadow-2xl text-center"
          >
            <div className="w-16 h-16 mx-auto mb-6 bg-rose-500/10 border border-rose-500/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-rose-500" />
            </div>

            <h2 className="text-xl font-extrabold text-amber-300 font-sans mb-3">
              {isRtl ? "خطایی رخ داده است" : "Something went wrong"}
            </h2>

            <p className="text-gray-400 text-sm mb-6 font-sans leading-relaxed">
              {isRtl
                ? "متاسفانه در بارگذاری این بخش مشکلی پیش آمده. لطفاً دوباره تلاش کنید."
                : "Unfortunately, an error occurred while loading this section. Please try again."}
            </p>

            {this.state.error && (
              <details className="mb-6 text-start">
                <summary className="text-xs text-gray-500 cursor-pointer font-mono select-none">
                  {isRtl ? "جزئیات فنی (برای توسعه‌دهندگان)" : "Technical Details (for developers)"}
                </summary>
                <pre className="mt-3 p-3 bg-black/50 border border-amber-500/10 rounded text-[10px] text-gray-300 overflow-x-auto font-mono">
                  {this.state.error.message}
                  {this.state.error.stack && `\n\n${this.state.error.stack}`}
                </pre>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.resetError}
                className="group flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-300 hover:to-amber-500 text-[#121212] font-bold text-sm rounded-full shadow-lg shadow-amber-950/20 transition-all cursor-pointer font-sans"
              >
                <RefreshCw className="w-4 h-4" />
                <span>{isRtl ? "تلاش مجدد" : "Try Again"}</span>
              </button>

              <button
                onClick={() => window.location.href = "/"}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#1e1e1e] border border-amber-500/20 hover:border-amber-500/40 hover:bg-amber-500/5 text-amber-300 font-bold text-sm rounded-full transition-all cursor-pointer font-sans"
              >
                <Home className="w-4 h-4" />
                <span>{isRtl ? "بازگشت به تالار اصلی" : "Back to Main Hall"}</span>
              </button>
            </div>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}